import { useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { copyText } from "@/lib/constants";
import { Field } from "@/components/common";
import { AVATARS, KidAvatar } from "@/components/Avatars";

const PINK = "var(--glow-kids)";

export function InviteForm({ hubId, onCreated }) {
  const [role, setRole] = useState("relative");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post(`/organizations/${hubId}/invites`, { role, email: email || null });
      setResult(data);
      if (data.email_sent) toast.success("Invite sent");
      onCreated();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  if (result) {
    return (
      <div className="space-y-4" data-testid="invite-result">
        {result.email_sent && <p className="rounded-lg border border-cyan-500/30 bg-cyan-500/10 px-3 py-2 text-sm font-semibold text-cyan-200" data-testid="invite-email-sent">Invite sent to {result.invite.email}</p>}
        {result.email_error && <p className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-3 py-2 text-sm font-semibold text-yellow-200" data-testid="invite-email-error">{result.email_error}</p>}
        <Field label={`${result.invite.role_label} invite link · valid 7 days, single use`}>
          <div className="flex gap-2">
            <input readOnly className="q-input font-mono text-xs" value={result.invite.link} onFocus={(e) => e.target.select()} data-testid="invite-link-input" />
            <button type="button" className="q-btn q-btn-secondary" data-testid="invite-link-copy-btn"
              onClick={async () => { if (await copyText(result.invite.link)) { setCopied(true); toast.success("Copied"); } }}>
              <Copy className="h-4 w-4" /> {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </Field>
        <button type="button" className="q-btn q-btn-ghost" onClick={() => { setResult(null); setEmail(""); setCopied(false); }} data-testid="invite-another-btn">Create another invite</button>
      </div>
    );
  }

  return (
    <form onSubmit={create} className="space-y-4" data-testid="invite-form">
      <div>
        <span className="q-label">Role</span>
        <div className="grid grid-cols-2 gap-2">
          {[["coparent", "Co-parent", "Same powers as you"], ["relative", "Relative", "Everything except Money"]].map(([k, l, d]) => (
            <button type="button" key={k} onClick={() => setRole(k)} data-testid={`invite-role-${k}`} aria-pressed={role === k}
              className="rounded-xl border p-3 text-left transition-[border-color,background-color] duration-150"
              style={role === k ? { borderColor: "rgb(var(--glow-family))", background: "rgba(var(--glow-family),0.1)" } : { borderColor: "rgb(var(--q-border-strong))" }}>
              <div className="text-sm font-bold text-q-text">{l}</div>
              <div className="text-xs text-q-faint">{d}</div>
            </button>
          ))}
        </div>
      </div>
      <Field label="Email (optional)" htmlFor="inv-email">
        <input id="inv-email" type="email" className="q-input" placeholder="We'll email them the link" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" />
      </Field>
      {error && <p className="q-error" data-testid="invite-form-error">{error}</p>}
      <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="invite-create-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Create invite link</button>
    </form>
  );
}

export function AvatarPicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-5 gap-2">
      {Object.keys(AVATARS).map((k) => (
        <button type="button" key={k} onClick={() => onChange(k)} aria-pressed={value === k} title={AVATARS[k].label} data-testid={`kid-avatar-option-${k}`}
          className="flex items-center justify-center rounded-xl p-1.5 transition-[background-color,box-shadow] duration-150"
          style={value === k ? { background: `rgba(${PINK},0.14)`, boxShadow: `inset 0 0 0 2px rgb(${PINK})` } : undefined}>
          <KidAvatar avatar={k} size={44} />
        </button>
      ))}
    </div>
  );
}

export function KidForm({ hubId, kid, onSaved }) {
  const [name, setName] = useState(kid?.name || "");
  const [avatar, setAvatar] = useState(kid?.avatar || "cat");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const pinOnly = !!kid;

  const submit = async (e) => {
    e.preventDefault();
    if (!pinOnly && !name.trim()) return setError("Give your kid a name");
    if (!/^\d{4}$/.test(pin)) return setError("PIN must be 4 digits");
    setBusy(true);
    try {
      pinOnly ? await api.patch(`/organizations/${hubId}/kids/${kid.id}`, { pin }) : await api.post(`/organizations/${hubId}/kids`, { name, avatar, pin });
      onSaved();
    } catch (err) { setError(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4" noValidate data-testid="kid-form">
      {!pinOnly && (<>
        <Field label="Name" htmlFor="kid-name"><input id="kid-name" className="q-input" value={name} onChange={(e) => { setName(e.target.value); setError(""); }} data-testid="kid-name-input" /></Field>
        <div><span className="q-label">Avatar</span><AvatarPicker value={avatar} onChange={setAvatar} /></div>
      </>)}
      <Field label={pinOnly ? "New 4-digit PIN" : "4-digit PIN"} htmlFor="kid-pin">
        <input id="kid-pin" className="q-input max-w-[160px] text-center text-lg tracking-[0.5em]" inputMode="numeric" maxLength={6}
          value={pin} onChange={(e) => { setPin(e.target.value); setError(""); }} data-testid="kid-pin-new-input" />
      </Field>
      {error && <p className="q-error" data-testid="kid-form-error">{error}</p>}
      <button className="q-btn q-btn-primary w-full" disabled={busy} data-testid="kid-form-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} {pinOnly ? "Change PIN" : "Add kid"}</button>
    </form>
  );
}
